import { createSlice } from '@reduxjs/toolkit'
import getDictionary from '../../lib/api/dictionary/getDictionary'
import getPhotoDictionary from '../../lib/api/dictionary/getPhotoDictionary'
import { getSelectedFamilyEventImage } from '../../lib/api/dictionary/getSelectedFamilyEventImage'
import { dictArr } from '../../lib/utils/dictArr'
import { dictViewCntSort, getSearchWord } from '../../lib/utils/dictViewCntSort'
import { AppDispatch, AppThunk } from '../store'
import { WjpediaCnt, WjpediaContents } from './types'

type WjpediaState = {
  isLoading: boolean
  isError: boolean
  errorMessage: string
  searchWord: string
  dictIdx: number // 탭 선택시 사전 넘버
  photoDict: any[]
  photoDictCnt: number
  wjpedia: WjpediaContents[]
  wjpediaCnt: WjpediaCnt[]
}

const initialState: WjpediaState = {
  isLoading: false,
  isError: false,
  errorMessage: '',
  searchWord: '',
  dictIdx: -1, // -1이 전체임. 헷갈림 주의
  photoDict: [], // 포토백과 라이트 결과
  photoDictCnt: 0, // 포토백과 라이트 검색결과 카운트
  wjpedia: [],
  wjpediaCnt: [],
}

const wjpediaSlice = createSlice({
  // // 액션 타입 문자열의 prefix로 들어간다. ex) "counter/~"
  name: 'wjpedia',
  // 초기값
  initialState,
  // 리듀서 맵
  reducers: {
    // "counter/setDiff" (리듀서 함수명이 액션이름이 된다)
    // 내부적으로 immer을 사용하기 때문에 객체 불변성을 지키지 않아도 된다.
    getWjpediaStart: (state) => {
      // 요청 시작
      state.isLoading = true
    },
    getWjpediaEnd: (state) => {
      // 요청 끝
      state.isLoading = false
    },
    getWjpediaError: (state, action) => {
      // 에러 발생
      state.isError = true
      state.isLoading = false
      state.errorMessage = action.payload
    },
    setDictIdx: (state, action) => {
      state.dictIdx = action.payload
    },
    setSearchWord: (state, action) => {
      state.searchWord = action.payload
    },
    setDictCnt: (state, action) => {
      // 사전별 검색건수 저장
      state.wjpediaCnt = action.payload
    },
    setContents: (state, action) => {
      // 데이터 저장
      state.wjpedia = action.payload
    },
    setPhotoDictCnt: (state, action) => {
      state.photoDictCnt = action.payload
    },
    setPhotoDictContents: (state, action) => {
      state.photoDict = action.payload
    },
  },
})

// 동음이의어를 찾아서 arrDictFirstContMap와 arrDictContMap 를 따로 구분
const settingDictDivide = (
  dictIdx: number,
  searchWord: string,
  dictionary: any,
  arrDictFirstContMap: any[],
  arrDictContMap: any[]
) => {
  // 회원 권한에 따른 사전 노출
  if (dictArr[dictIdx].unMemberViewYn === 'N') {
    return
  }

  if (dictionary.hasOwnProperty(dictArr[dictIdx].apiDictContNm)) {
    const dictResult = dictionary[dictArr[dictIdx].apiDictContNm].myArrayList

    for (const idx in dictResult) {
      dictResult[idx].map.dictIdx = dictIdx // 사전번호(인덱스) 저장

      // 검색어와 일치하면(동음이의어이면)
      if (getSearchWord(dictResult[idx].map).trim() === searchWord) {
        dictResult[idx].map.isFirst = true // 우선순위 저장(나중에 정렬할때 쓰임)
        arrDictFirstContMap.push(dictResult[idx])
      } else {
        dictResult[idx].map.isFirst = false
        arrDictContMap.push(dictResult[idx])
      }
    }
  }
}

// 사전별 카운트 뽑아내는 함수
const settingDictCnt = async (searchWord: string, result: any, dispatch: AppDispatch) => {
  const totalDict = result.result.map.RESP_RESULT.map // 으... 형식 진짜 별로다...
  const dictCntObj: any[] = []
  let totalCnt = 0

  const arrDictFirstContMap: any[] = [] // 동음이의어 우선순위
  const arrDictContMap: any[] = [] //  그 외 경우

  for (const dictIdx in dictArr) {
    if (dictArr[dictIdx].unMemberViewYn === 'N') {
      dictCntObj.push({
        dictName: dictArr[dictIdx].dictKorNm,
        dictCd: dictArr[dictIdx].dictCd,
        dictCnt: 0,
      })

      continue
    }
    // dictArr 순서대로 사전을 찾는다.
    if (totalDict.hasOwnProperty(dictArr[dictIdx].apiDictNm)) {
      const dictionary = totalDict[dictArr[dictIdx].apiDictNm].map

      let dictCnt = 0 // 아래 코드는 복붙 ㅎㅎ 아... 이게 API 호출 결과를 보면 알겠지만 이렇게 구현할 수 밖에 없네
      // 공공 DB인 녀석들
      if (dictionary.hasOwnProperty(dictArr[dictIdx].dictCd + '_CNT')) {
        dictCnt = Number(dictionary[dictArr[dictIdx].dictCd + '_CNT'])
      }
      // 우리말샘 (이녀석 이력 쌓을때 KOR_SAM으로 쌓아서 그럼... 근데 받는건 KORSAM... 어쩔 수 없었다)
      else if (dictionary.hasOwnProperty('KORSAM_CNT')) {
        dictCnt = Number(dictionary['KORSAM_CNT'])
      }
      // 공공 DB가 아닌 녀석(웅진백과, 국어사전 등)
      else if (
        'PUBLIC_OPEN_DIC' !== dictArr[dictIdx].apiDictNm &&
        dictionary.hasOwnProperty('SEARCH_CNT')
      ) {
        dictCnt = Number(dictionary.SEARCH_CNT)
      }
      // 음???
      else {
        dictCnt = 0
      }
      totalCnt += dictCnt
      dictCntObj.push({
        dictName: dictArr[dictIdx].dictKorNm,
        dictCd: dictArr[dictIdx].dictCd,
        dictCnt: dictCnt,
      })
      settingDictDivide(
        Number(dictIdx),
        searchWord,
        dictionary,
        arrDictFirstContMap,
        arrDictContMap
      )
    }
  }

  dictCntObj.unshift({
    dictName: '전체',
    dictCnt: totalCnt,
  })

  dispatch(setDictCnt(dictCntObj))
  const sortResult = await dictViewCntSort(arrDictFirstContMap, arrDictContMap)
  dispatch(setContents(sortResult))
}

export const fetchWjpedia =
  (searchWord: string): AppThunk =>
  //@ts-ignore
  async (dispatch: AppDispatch) => {
    try {
      dispatch(getWjpediaStart())

      const result = await getDictionary(searchWord)
      //if (Object.keys(result).length === 0) throw new Error('Search API DOWN')
      if (result.result?.map?.status === 'BAD_GATEWAY') throw new Error('BAD_GATEWAY')
      await settingDictCnt(searchWord, result, dispatch)

      // 포토백과 요청 추가
      const { photoDictCnt, photoDictContents } = await getPhotoDictionary(searchWord)

      dispatch(setPhotoDictCnt(photoDictCnt))
      dispatch(setPhotoDictContents(photoDictContents))

      dispatch(getWjpediaEnd())
    } catch (e: any) {
      console.log(e)
      dispatch(getWjpediaError(e.message))
    }
  }

export const fetchSelectedFamilyEventImage =
  (eventSeq: string, customerNumber: string): AppThunk =>
  //@ts-ignore
  async (dispatch: AppDispatch) => {
    try {
      dispatch(getWjpediaStart())

      // 포토백과 요청 추가
      const { photoDictCnt, photoDictContents } = await getSelectedFamilyEventImage(
        eventSeq,
        customerNumber
      )

      dispatch(setPhotoDictCnt(photoDictCnt))
      dispatch(setPhotoDictContents(photoDictContents))

      dispatch(getWjpediaEnd())
    } catch (e: any) {
      console.log(e)
      dispatch(getWjpediaError(e.message))
    }
  }

// 그렇게 만든 슬라이스 객체에서 액션과 리듀서는 아래와 같이 가져올 수 있다.
export const {
  getWjpediaStart,
  getWjpediaEnd,
  getWjpediaError,
  setDictIdx,
  setSearchWord,
  setDictCnt,
  setContents,
  setPhotoDictCnt,
  setPhotoDictContents,
} = wjpediaSlice.actions
export default wjpediaSlice.reducer
